import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

/**
 * Input — base text-input primitive. Variants:
 *   - default : standard form input, rounded-lg
 *   - ghost   : borderless, transparent, for inline-edit / table-cell scenarios
 *   - chat    : softer fill, rounded-2xl, glow-on-focus (composed by ChatInput)
 *   - search  : pill, icon-prefix slot via padding (composed by ExploreSearchBar)
 *
 * Sizes: sm / default / lg
 *
 * All colors via consumer CSS vars; no hardcoded brand values.
 */
export const inputVariants = cva(
  [
    "flex w-full bg-background text-foreground ring-offset-background",
    "transition-all duration-180 ease-smooth-out",
    "file:border-0 file:bg-transparent file:text-sm file:font-medium",
    "placeholder:text-muted-foreground",
    // The focus RING OFFSET lives on the variants that actually draw a ring,
    // not here. When the base owned it, every faceless variant had to REMEMBER
    // to zero it, and three of four forgot: Tailwind emits ring-offset as its
    // own box-shadow, so a transparent field with ring-0 still painted a 2px
    // --background box with no ring inside it. Forgetting to ADD an offset is a
    // flush ring, which is cosmetic; forgetting to ZERO one is a visible box on
    // a field that is meant to be invisible. Putting it on the variants makes
    // the failure mode the harmless one.
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    "disabled:cursor-not-allowed disabled:opacity-50",
    // Error morph — when a wrapper (e.g. FormControl) sets aria-invalid, the
    // border + focus ring turn destructive and the existing transition above
    // makes idle -> error -> valid a smooth color morph, not a snap. Lets
    // consumers stop hand-coloring error borders.
    "aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive",
  ].join(" "),
  {
    variants: {
      variant: {
        default: "border border-input rounded-lg focus-visible:ring-offset-2",
        ghost: "border-0 bg-transparent rounded-none px-0 focus-visible:ring-0 focus-visible:ring-offset-0",
        chat:
          "border border-transparent bg-muted/40 rounded-2xl shadow-sm focus-visible:bg-background focus-visible:border-input focus-visible:shadow-glow focus-visible:ring-offset-2",
        search: "border border-input bg-muted/40 rounded-full focus-visible:ring-offset-2",
      },
      size: {
        sm: "h-9 px-3 text-xs",
        default: "h-10 px-3 py-2 text-sm",
        lg: "h-12 px-4 text-base",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, size, type, ...props }, ref) => (
    <input
      type={type}
      className={cn(inputVariants({ variant, size }), className)}
      ref={ref}
      {...props}
    />
  ),
);

Input.displayName = "Input";
